/** Console shell — enterprise chrome: fixed aeblack sidebar, solid top bar,
 *  and a workspace container so page components never float directly over the
 *  raw background (doc 11; aeGov DLS tokens throughout). */
import { type FormEvent, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@servicesai/api-client';
import {
  GlassButton,
  GlassCard,
  GlassSelect,
  LangToggle,
  ScenicBackdrop,
  UpdateBanner,
  useI18n,
  type Localized,
} from '@servicesai/ui';
import {
  QUICK_LOGIN_STAFF,
  admin,
  getStaffToken,
  issueQuickLoginToken,
  quickLoginStaffIdFromToken,
  setStaffToken,
  type QuickLoginStaffId,
} from './lib/api.js';
import { S } from './strings.js';
import { DashboardPage } from './pages/Dashboard.js';
import { QueuesPage } from './pages/Queues.js';
import { CasePage } from './pages/Case.js';
import { ApprovalsPage } from './pages/Approvals.js';
import { ServicesPage } from './pages/Services.js';
import { CustomersPage } from './pages/CustomerDirectory.js';
import { CustomerPage } from './pages/CustomerCrm.js';
import { AgentsPage } from './pages/Agents.js';
import { AgentDetailPage } from './pages/AgentDetail.js';
import { ProactivePage } from './pages/Proactive.js';
import { ConversationTranscriptPage } from './pages/ConversationTranscript.js';
import { CommandPalette } from './components/CommandPalette.js';
import { ShortcutsOverlay } from './components/ShortcutsOverlay.js';
import { ConsoleSidebar, SidebarDrawer, readSidebarMode, SIDEBAR_MODE_KEY } from './components/ConsoleSidebar.js';
import { Icon } from './components/elevated/icons.js';
import { NAV_GROUPS } from './lib/navigation.js';
import {
  completeStaffSsoCallback,
  logoutStaffSso,
  refreshStaffSso,
  staffSsoConfigured,
  startStaffSso,
} from './lib/staffSso.js';

const BuilderPage = lazy(() => import('./pages/Builder.js').then((module) => ({ default: module.BuilderPage })));
const GovSyncPage = lazy(() => import('./pages/GovSync.js').then((module) => ({ default: module.GovSyncPage })));
const AuditPage = lazy(() => import('./pages/Audit.js').then((module) => ({ default: module.AuditPage })));
const TracesPage = lazy(() => import('./pages/Traces.js').then((module) => ({ default: module.TracesPage })));
const JourneysPage = lazy(() => import('./pages/Journeys.js').then((module) => ({ default: module.JourneysPage })));
const DocumentsPage = lazy(() => import('./pages/Documents.js').then((module) => ({ default: module.DocumentsPage })));
const PaymentsPage = lazy(() => import('./pages/Payments.js').then((module) => ({ default: module.PaymentsPage })));
const NotificationsPage = lazy(() => import('./pages/Notifications.js').then((module) => ({ default: module.NotificationsPage })));
const AdminPage = lazy(() => import('./pages/Admin.js').then((module) => ({ default: module.AdminPage })));
const IntegrationsPage = lazy(() => import('./pages/Integrations.js').then((module) => ({ default: module.IntegrationsPage })));
const IntegrationDetailPage = lazy(() => import('./pages/IntegrationDetail.js').then((module) => ({ default: module.IntegrationDetailPage })));
const HandoverPage = lazy(() => import('./pages/handover/HandoverDesk.js').then((module) => ({ default: module.HandoverDesk })));
const CxoneSettingsPage = lazy(() => import('./pages/CxoneSettings.js').then((module) => ({ default: module.CxoneSettingsPage })));
const CxoneAuthCallback = lazy(() => import('./cxone/CxoneAuthCallback.js').then((module) => ({ default: module.CxoneAuthCallback })));
const HousingBundlesPage = lazy(() => import('./pages/HousingBundles.js').then((module) => ({ default: module.HousingBundlesPage })));
// Pre-approval review has a better home in the Housing Management Console, and
// these screens will hand over to it — but not in the release that first deploys
// that console. Staff SSO for it lands on a redirect URI that has to be
// registered in the Keycloak realm, which is configuration outside this
// repository, so until somebody has signed in to `/housing/` in production these
// stay the working path. `HousingConsoleMoved` is the signpost that replaces them
// once that is confirmed; it is one import and five routes.
const FieldResearchHomePage = lazy(() =>
  import('./pages/field-research/FieldResearchHome.js').then((module) => ({ default: module.FieldResearchHomePage })),
);
const FieldResearchQueuePage = lazy(() =>
  import('./pages/field-research/FieldResearchQueue.js').then((module) => ({ default: module.FieldResearchQueuePage })),
);
const FieldResearchCasePage = lazy(() =>
  import('./pages/field-research/FieldResearchCase.js').then((module) => ({ default: module.FieldResearchCasePage })),
);
const HousingCommitteeSessionPage = lazy(() =>
  import('./pages/housing/HousingCommitteeSession.js').then((module) => ({
    default: module.HousingCommitteeSessionPage,
  })),
);
const HousingFinancingDeskPage = lazy(() =>
  import('./pages/housing/HousingFinancingDesk.js').then((module) => ({
    default: module.HousingFinancingDeskPage,
  })),
);
const MinistryCrmPage = lazy(() => import('./pages/MinistryCrm.js').then((module) => ({ default: module.MinistryCrmPage })));
const MinistryCrmRecordPage = lazy(() => import('./pages/MinistryCrmRecord.js').then((module) => ({ default: module.MinistryCrmRecordPage })));
const BundlesListPage = lazy(() => import('./pages/bundles/BundlesList.js').then((module) => ({ default: module.BundlesListPage })));
const BundleEditorPage = lazy(() => import('./pages/bundles/BundleEditor.js').then((module) => ({ default: module.BundleEditorPage })));
const ExecutiveShell = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveShell })));
const ExecutiveHomePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveHomePage })));
const ExecutiveServicesPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveServicesPage })));
const ExecutivePredictivePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutivePredictivePage })));
const ExecutiveCustomersPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveCustomersPage })));
const ExecutiveDemographicsPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveDemographicsPage })));
const ExecutiveRecordsPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveRecordsPage })));
const ExecutiveExplorePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveExplorePage })));
const ExecutiveRecordPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveRecordPage })));
const ExecutiveCitizenPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveCitizenPage })));
const ExecutiveCopilotPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveCopilotPage })));
const ExecutiveUsagePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveUsagePage })));
const ExecutiveHourlyPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveHourlyPage })));
const ExecutiveVocPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveVocPage })));
const ExecutiveCrmPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveCrmPage })));
const ExecutiveCompletionPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveCompletionPage })));
const ExecutiveCompletionServicePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveCompletionServicePage })));
const ExecutivePodPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutivePodPage })));
const ExecutiveServiceTimePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveServiceTimePage })));
const ExecutivePeakPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutivePeakPage })));
const ExecutiveDigitalImpactPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveDigitalImpactPage })));
const ExecutiveNocPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveNocPage })));
const ExecutiveHappinessPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveHappinessPage })));
const ExecutivePersonalizePage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutivePersonalizePage })));
const ExecutiveSlaPage = lazy(() => import('./executive/index.js').then((module) => ({ default: module.ExecutiveSlaPage })));

/** Current page title for the top bar, from the first path segment. */
const SEGMENT_TITLE: Record<string, Localized> = {
  executive: { en: 'Executive Dashboard', ar: 'لوحة القيادة التنفيذية' },
  dashboard: S.dashboard,
  queues: S.queues,
  cases: S.queues,
  housing: { en: 'Manzili governance', ar: 'حوكمة منزلي' },
  'field-research': { en: 'Field research', ar: 'البحث الميداني' },
  approvals: S.approvals,
  customers: S.customers,
  conversations: S.customers,
  crm: { en: 'Ministry CRM', ar: 'إدارة علاقات المتعاملين' },
  handover: S.handover,
  proactive: S.proactive,
  services: S.services,
  agents: S.agents,
  integrations: { en: 'Integrations', ar: 'التكاملات' },
  documents: { en: 'Documents', ar: 'المستندات' },
  payments: { en: 'Payments', ar: 'المدفوعات' },
  govsync: { en: 'Government sync', ar: 'الربط الحكومي' },
  notifications: { en: 'Notifications', ar: 'الإشعارات' },
  audit: { en: 'Audit explorer', ar: 'مستكشف التدقيق' },
  traces: { en: 'Conversation traces', ar: 'تتبّع المحادثات' },
  admin: { en: 'Administration', ar: 'الإدارة' },
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

export function App() {
  const { l } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState<{ displayName: string; roles: string[] } | null>(null);
  const [token, setToken] = useState(getStaffToken());
  // 'unauthenticated' means the API rejected the current token (401). We gate
  // the whole console on it so staff get an actionable prompt instead of a
  // cryptic "could not load" on every page (deployed envs seed real tokens —
  // the baked-in dev default never matches). Other failures (network / 5xx)
  // fall through to 'ok' so pages can surface their own error states.
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'unauthenticated'>('checking');
  // Whether the staff has submitted a token through the sign-in gate this
  // session — drives the gate's "invalid token" feedback and busy state.
  const [gateTried, setGateTried] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readSidebarMode() === 'collapsed');
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [branding, setBranding] = useState<Record<string, unknown>>({});
  const ssoEnabled = staffSsoConfigured();

  useEffect(() => {
    let cancelled = false;
    const synchronize = async () => {
      try {
        const completed = await completeStaffSsoCallback();
        const refreshed = completed || await refreshStaffSso();
        if (!cancelled && refreshed) setToken(getStaffToken());
      } catch {
        if (!cancelled) setAuthState('unauthenticated');
      }
    };
    void synchronize();
    const timer = window.setInterval(() => void synchronize(), 45_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAuthState('checking');
    if (!token) {
      setMe(null);
      setAuthState('unauthenticated');
      return () => {
        cancelled = true;
      };
    }
    admin()
      .me()
      .then((user) => {
        if (cancelled) return;
        setMe(user);
        setAuthState('ok');
        admin().platformFeatures().then((result) => {
          if (!cancelled) {
            setFeatureFlags(result.featureFlags);
            setBranding(result.branding);
          }
        }).catch(() => undefined);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMe(null);
        setAuthState(err instanceof ApiError && err.status === 401 ? 'unauthenticated' : 'ok');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const consoleName = branding.consoleName;
    if (consoleName && typeof consoleName === 'object' && 'en' in consoleName) {
      document.title = String((consoleName as { en: unknown }).en);
    }
    const accent = typeof branding.accentColor === 'string' ? branding.accentColor : '';
    if (/^#[0-9a-f]{6}$/i.test(accent)) {
      document.documentElement.style.setProperty('--tenant-accent', accent);
    }
  }, [branding]);

  // Global keyboard shortcuts (point 18): ⌘K / '/' open the palette, 'g x'
  // sequences jump to primary sections, '?' shows the cheat-sheet. Keys are
  // ignored while typing in fields or while an overlay is open.
  useEffect(() => {
    let pendingGAt = 0;
    const isTypingTarget = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable);
    const onKey = (event: KeyboardEvent) => {
      // The executive space ships its own search palette and owns ⌘K + '/'
      // while active; the console-wide palette stays out of its way.
      if (window.location.pathname.startsWith('/executive')) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        setCommandPaletteOpen(true);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (commandPaletteOpen || shortcutsOpen || drawerOpen) return;
      const now = Date.now();
      if (event.key === 'g') {
        pendingGAt = now;
        return;
      }
      if (pendingGAt > 0 && now - pendingGAt < 900) {
        pendingGAt = 0;
        const targets: Record<string, string> = { q: '/queues', a: '/agents', h: '/housing', d: '/dashboard' };
        const to = targets[event.key];
        if (to) {
          event.preventDefault();
          navigate(to);
          return;
        }
      }
      if (event.key === '/') {
        event.preventDefault();
        setCommandPaletteOpen(true);
      } else if (event.key === '?') {
        event.preventDefault();
        setShortcutsOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, commandPaletteOpen, shortcutsOpen, drawerOpen]);

  const applyToken = (next: string) => {
    setStaffToken(next);
    setToken(next);
  };

  const loginAsStaff = async (staffId: QuickLoginStaffId) => {
    applyToken(await issueQuickLoginToken(staffId));
  };

  const submitFromGate = async (staffId: QuickLoginStaffId) => {
    setGateTried(false);
    setAuthState('checking');
    try {
      await loginAsStaff(staffId);
    } catch {
      setGateTried(true);
      setAuthState('unauthenticated');
    }
  };

  // Never mount protected routes until staff authentication succeeds. Besides
  // avoiding a flash of restricted content, this prevents every page effect
  // from issuing doomed requests while /me is still being checked.
  if (authState !== 'ok') {
    return (
      <SignInGate
        onSubmit={submitFromGate}
        onSso={() => void startStaffSso()}
        ssoEnabled={ssoEnabled}
        checking={authState === 'checking'}
        invalid={gateTried && authState === 'unauthenticated'}
      />
    );
  }

  if (location.pathname === '/auth/cxone/callback') {
    return <Suspense fallback={<div className="min-h-screen bg-aeblack-100" aria-busy="true" />}><CxoneAuthCallback /></Suspense>;
  }

  const segment = location.pathname.split('/')[1] ?? '';
  const pageTitle = SEGMENT_TITLE[segment] ?? S.dashboard;
  const brandInitials = typeof branding.initials === 'string' ? branding.initials.slice(0, 3) : 'SA';
  const brandName =
    branding.consoleName && typeof branding.consoleName === 'object'
      ? l(branding.consoleName as Localized)
      : l(S.console);
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !me || !item.roles || item.roles.some((role) => me.roles.includes(role)),
    ).filter((item) => !item.featureFlag || featureFlags[item.featureFlag] !== false),
  })).filter((group) => group.items.length > 0);

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDEBAR_MODE_KEY, next ? 'collapsed' : 'expanded');
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  };

  const staffSelect = token.startsWith('Bearer ') ? (
    <div className="flex items-center gap-2">
      <span className="hidden text-xs font-bold text-ink sm:inline">{me?.displayName ?? l({ en: 'Staff SSO', ar: 'دخول الموظف الموحد' })}</span>
      <button
        type="button"
        className="aegov-btn btn-outline btn-xs inline-flex min-h-10 items-center justify-center"
        onClick={() => {
          // Ends the Keycloak realm session too (front-channel logout), so the
          // next sign-in genuinely re-authenticates.
          setStaffToken('');
          setToken('');
          logoutStaffSso();
        }}
      >
        {l({ en: 'Sign out', ar: 'تسجيل الخروج' })}
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      {/* DLS select: the .aegov-form-control component supplies the white field,
          2px primary inset ring, camel focus ring and the gold chevron arrow. */}
      <GlassSelect
        value={quickLoginStaffIdFromToken(token) ?? ''}
        onChange={(e) => {
          void loginAsStaff(e.target.value as QuickLoginStaffId).catch(() => undefined);
        }}
        aria-label={l(S.signedInAs)}
        className="w-36 sm:w-72"
      >
        <option value="" disabled>{l(S.selectStaffMember)}</option>
        {QUICK_LOGIN_STAFF.map((staff) => (
          <option key={staff.id} value={staff.id}>
            {staff.name} — {staff.roles}
          </option>
        ))}
      </GlassSelect>
      {token && (
        <button
          type="button"
          className="aegov-btn btn-outline btn-xs inline-flex min-h-10 items-center justify-center"
          onClick={() => {
            setStaffToken('');
            setToken('');
          }}
        >
          {l({ en: 'Sign out', ar: 'تسجيل الخروج' })}
        </button>
      )}
    </div>
  );

  return (
    <div className="enterprise flex min-h-screen bg-aeblack-100">
      {/* WCAG 2.4.1 Bypass Blocks — first tab stop jumps past nav + sidebar. */}
      <a href="#main" className="skip-link">
        {l(S.skipToContent)}
      </a>
      <ScrollToTop />
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        roles={me?.roles ?? []}
      />
      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* ---- Enterprise sidebar: fixed, aeblack, grouped navigation ---- */}
      <ConsoleSidebar
        groups={visibleGroups}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        brandInitials={brandInitials}
        brandName={brandName}
      />
      <SidebarDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        groups={visibleGroups}
        brandInitials={brandInitials}
        brandName={brandName}
        triggerRef={menuButtonRef}
      />

      {/* ---- Content column ---- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <UpdateBanner />

        <header className="sticky top-0 z-30 border-b border-aeblack-100 bg-white/95 backdrop-blur">
          <div aria-hidden className="dls-strip" />
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
            <div className="flex min-w-0 items-center gap-2.5">
              <button
                ref={menuButtonRef}
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-(--radius-control) text-ink transition hover:bg-aeblack-50 lg:hidden"
                aria-label={l({ en: 'Open navigation menu', ar: 'فتح قائمة التنقل' })}
              >
                <Icon name="menu" size={18} />
              </button>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-(--radius-control) bg-aegold-500 text-xs font-black text-aeblack-900 lg:hidden" style={{ backgroundColor: 'var(--tenant-accent, var(--color-aegold-500))' }}>
                {brandInitials}
              </span>
              {/* DLS breadcrumb component (nav.aegov-breadcrumb) — pages own
                  their h1, the top bar shows the location trail. */}
              <nav className="aegov-breadcrumb hidden min-w-0 sm:block" aria-label={l({ en: 'Breadcrumb', ar: 'مسار التنقل' })}>
                <ol>
                  <li>
                    <Link to="/queues">{l(S.console)}</Link>
                  </li>
                  <li>
                    <svg
                      aria-hidden
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </li>
                  <li>
                    <span aria-current="page">{l(pageTitle)}</span>
                  </li>
                </ol>
              </nav>
              {import.meta.env.DEV && (
                <span className="shrink-0 rounded-full border border-aegold-500/50 bg-aegold-500/10 px-2 py-0.5 text-[9px] font-extrabold tracking-[0.14em] text-aegold-700 uppercase">
                  {l(S.envSandbox)}
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setCommandPaletteOpen(true)}
                className="grid h-10 w-10 cursor-pointer place-items-center rounded-(--radius-control) text-ink transition hover:bg-aeblack-50 xl:hidden"
                aria-label={l({ en: 'Open command palette', ar: 'فتح لوحة الأوامر' })}
              >
                <Icon name="search" size={18} />
              </button>
              <button
                type="button"
                onClick={() => setCommandPaletteOpen(true)}
                className="aegov-btn btn-secondary btn-soft btn-xs hidden min-h-10 items-center justify-center gap-2 xl:inline-flex"
                aria-label={l({ en: 'Open command palette', ar: 'فتح لوحة الأوامر' })}
              >
                <span>{l({ en: 'Search', ar: 'بحث' })}</span>
                <kbd className="rounded border border-aeblack-200 bg-white px-1.5 py-0.5 font-mono text-[9px] text-ink-faint">
                  ⌘K
                </kbd>
              </button>
              <span className="hidden text-[11px] font-semibold text-ink-faint sm:inline">{l(S.signedInAs)}</span>
              {staffSelect}
              {me && (
                <span className="aegov-badge badge-info hidden md:inline-flex">{me.roles.join(' · ')}</span>
              )}
              <LangToggle />
            </div>
          </div>
          {/* Compact nav for small screens (sidebar hidden below lg) */}
          <nav className="flex gap-1 overflow-x-auto border-t border-aeblack-100 px-3 py-1.5 lg:hidden">
            {visibleGroups.flatMap((g) => g.items).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    'inline-flex min-h-10 items-center rounded-(--radius-control) px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors',
                    isActive ? 'bg-aeblack-800 text-white' : 'text-ink-soft hover:bg-aeblack-50',
                  ].join(' ')
                }
              >
                {l(item.label)}
              </NavLink>
            ))}
          </nav>
        </header>

        {/* Workspace container: a defined surface between the chrome and the
            page's cards, so components never sit on the raw background. */}
        <main id="main" className="flex-1 px-3 py-3 sm:px-5 sm:py-4" key={`${token}:${location.pathname}`}>
          <div className="min-h-full w-full rounded-xl border border-aeblack-200/70 bg-whitely-200 p-3 shadow-[inset_0_1px_0_rgb(255_255_255/0.8),0_1px_2px_rgb(14_15_18/0.04)] sm:p-5">
            <Suspense fallback={<div className="h-96 animate-pulse rounded-xl border border-aeblack-100 bg-white" aria-busy="true" />}>
              <Routes>
              <Route path="/" element={<Navigate to="/queues" replace />} />
              <Route path="/executive" element={<ExecutiveShell />}>
                <Route index element={<ExecutiveHomePage />} />
                <Route path="services" element={<ExecutiveServicesPage />} />
                <Route path="predictive" element={<ExecutivePredictivePage />} />
                <Route path="customers" element={<ExecutiveCustomersPage />} />
                <Route path="demographics" element={<ExecutiveDemographicsPage />} />
                <Route path="explore" element={<ExecutiveExplorePage />} />
                <Route path="kpi-agent" element={<ExecutiveCopilotPage />} />
                {/* Legacy deep links keep working after the KPI Agent rebrand. */}
                <Route path="copilot" element={<Navigate to="/executive/kpi-agent" replace />} />
                <Route path="records" element={<ExecutiveRecordsPage />} />
                <Route path="records/:recordId" element={<ExecutiveRecordPage />} />
                <Route path="citizens/:citizenHash" element={<ExecutiveCitizenPage />} />
                <Route path="customers/records" element={<ExecutiveRecordsPage />} />
                <Route path="usage" element={<ExecutiveUsagePage />} />
                <Route path="hourly" element={<ExecutiveHourlyPage />} />
                <Route path="voc" element={<ExecutiveVocPage />} />
                <Route path="crm" element={<ExecutiveCrmPage />} />
                <Route path="sla" element={<ExecutiveSlaPage />} />
                <Route path="completion" element={<ExecutiveCompletionPage />} />
                <Route path="completion/:serviceSlug" element={<ExecutiveCompletionServicePage />} />
                <Route path="pod" element={<ExecutivePodPage />} />
                <Route path="service-time" element={<ExecutiveServiceTimePage />} />
                <Route path="peak" element={<ExecutivePeakPage />} />
                <Route path="digital-impact" element={<ExecutiveDigitalImpactPage />} />
                <Route path="noc" element={<ExecutiveNocPage />} />
                <Route path="happiness" element={<ExecutiveHappinessPage />} />
                <Route path="personalize" element={<ExecutivePersonalizePage />} />
              </Route>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/queues" element={<QueuesPage />} />
              <Route path="/cases/:reference" element={<CasePage />} />
              {/* Post-approval Manzili delivery stays here. The bundle phases, the
                  financing contract and the design gates have no equivalent in the
                  Housing Management Console, so redirecting them would delete a
                  working surface rather than move it. */}
              <Route path="/housing" element={<HousingBundlesPage />} />
              {/* Both sit above the :bundleId route so their static paths win. */}
              <Route path="/housing/committee" element={<HousingCommitteeSessionPage />} />
              <Route
                path="/housing/financing-contracts"
                element={<HousingFinancingDeskPage />}
              />
              <Route path="/housing/:bundleId" element={<HousingBundlesPage />} />
              <Route path="/field-research" element={<FieldResearchHomePage />} />
              <Route path="/field-research/all" element={<FieldResearchQueuePage kind="all" />} />
              <Route path="/field-research/social" element={<FieldResearchQueuePage kind="social" />} />
              <Route path="/field-research/technical" element={<FieldResearchQueuePage kind="technical" />} />
              <Route path="/field-research/cases/:reference" element={<FieldResearchCasePage />} />
              <Route path="/approvals" element={<ApprovalsPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:id" element={<CustomerPage />} />
              <Route path="/crm" element={<MinistryCrmPage />} />
              <Route path="/crm/records/:set/:recordId" element={<MinistryCrmRecordPage />} />
              <Route path="/handover" element={<HandoverPage />} />
              <Route path="/handover/:conversationId" element={<HandoverPage />} />
              <Route path="/proactive" element={<ProactivePage />} />
              <Route path="/conversations/:conversationId" element={<ConversationTranscriptRoute />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<BuilderPage />} />
              <Route path="/bundles" element={<BundlesListPage />} />
              <Route path="/bundles/:slug" element={<BundleEditorPage />} />
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/agents/:slug" element={<AgentDetailPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/govsync" element={<GovSyncPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/journeys" element={<JourneysPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/traces" element={<TracesPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/integrations/cxone" element={<CxoneSettingsPage />} />
              <Route path="/integrations/:adapterId" element={<IntegrationDetailPage />} />
              </Routes>
            </Suspense>
          </div>
        </main>

        <footer className="aegov-footer !bg-transparent !pt-0">
          <div className="footer-bottom flex w-full flex-col items-center justify-between gap-1 px-6 py-3.5 text-center text-[11px] text-ink-faint sm:flex-row sm:text-start">
            <span>© 2026 {l(S.ministry)} — ServicesAI · by Agentic OS</span>
            <span>Designed on the UAE Design Language System</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ConversationTranscriptRoute() {
  const { conversationId = '' } = useParams();
  const { l } = useI18n();
  return (
    <ConversationTranscriptPage
      conversationId={conversationId}
      backTo="/customers"
      backLabel={l(S.customers)}
    />
  );
}

/** Full-screen quick-login prompt shown when staff authentication is required. */
function SignInGate({
  onSubmit,
  onSso,
  ssoEnabled,
  checking,
  invalid,
}: {
  onSubmit: (staffId: QuickLoginStaffId) => void;
  onSso: () => void;
  ssoEnabled: boolean;
  checking: boolean;
  invalid: boolean;
}) {
  const { l } = useI18n();
  const [value, setValue] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!value || checking) return;
    onSubmit(value as QuickLoginStaffId);
  };

  return (
    <div className="min-h-screen">
      <ScenicBackdrop />
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
        <GlassCard className="p-7" variant="glass-strong">
          <div className="mb-5 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-(--radius-control) bg-aegold-500 text-sm font-black text-aeblack-900">
              SA
            </span>
            <div className="leading-tight">
              <div className="text-sm font-extrabold tracking-tight">{l(S.console)}</div>
              <div className="text-[11px] text-ink-faint">{l(S.ministry)}</div>
            </div>
            <div className="ms-auto">
              <LangToggle />
            </div>
          </div>

          <h1 className="text-h4 mb-2">{l(S.authGateTitle)}</h1>
          <p className="mb-5 text-sm text-ink-soft">
            {ssoEnabled
              ? l({ en: 'Sign in with your ministry Microsoft account (single sign-on shared with moei.agentic.ae). Multi-factor authentication is required before the console grants access.', ar: 'سجّل الدخول بحساب مايكروسوفت الوزاري (الدخول الموحد المشترك مع moei.agentic.ae). المصادقة متعددة العوامل مطلوبة قبل منح الوصول إلى وحدة التحكم.' })
              : l(S.authGateHint)}
          </p>

          {invalid && (
            <div className="aegov-alert alert-error alert-sm mb-4" role="alert">
              <div className="alert-content">{l(S.authGateInvalid)}</div>
            </div>
          )}

          {ssoEnabled && (
            <GlassButton type="button" variant="primary" className="mb-4 w-full" onClick={onSso}>
              <span className="inline-flex items-center gap-2.5">
                <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden>
                  <rect x="1" y="1" width="10" height="10" fill="#f35325" />
                  <rect x="12" y="1" width="10" height="10" fill="#81bc06" />
                  <rect x="1" y="12" width="10" height="10" fill="#05a6f0" />
                  <rect x="12" y="12" width="10" height="10" fill="#ffba08" />
                </svg>
                {l({ en: 'Sign in with Microsoft', ar: 'تسجيل الدخول عبر مايكروسوفت' })}
              </span>
            </GlassButton>
          )}

          {ssoEnabled && (
            <div className="mb-4 flex items-center gap-3 text-[10px] font-bold text-ink-faint uppercase">
              <span className="h-px flex-1 bg-aeblack-100" />
              {l({ en: 'Quick login', ar: 'الدخول السريع' })}
              <span className="h-px flex-1 bg-aeblack-100" />
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <GlassSelect
              autoFocus={!ssoEnabled}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label={l(S.selectStaffMember)}
              className="w-full"
            >
              <option value="" disabled>{l(S.selectStaffMember)}</option>
              {QUICK_LOGIN_STAFF.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {staff.name} — {staff.roles}
                </option>
              ))}
            </GlassSelect>
            <GlassButton type="submit" variant="primary" disabled={!value || checking} className="w-full">
              {checking ? l(S.authGateChecking) : l(S.authGateSubmit)}
            </GlassButton>
          </form>
        </GlassCard>
      </div>
    </div>
  );
}
